import {
  DEFAULT_LEVEL_LABELS,
  labelAt,
  normalizeLabels,
} from "../page/Subjects/detail/hierarchyUtils"
import {
  LevelFieldNames,
  LevelFieldValues,
} from "../page/Questions/components/LevelSelectors"
import { HierarchyTopicSelection } from "../types/hierarchyTopic"

const LEVEL_NAME_KEYS: (keyof LevelFieldNames)[] = [
  "level_1",
  "level_2",
  "level_3",
  "level_4",
]

/** Stable key for deduplicating topic slots by their hierarchy path. */
export const hierarchyTopicKey = (
  topic: Pick<
    HierarchyTopicSelection,
    | "topic_id"
    | "level_2_id"
    | "level_3_id"
    | "level_4_id"
    | "name"
  >
): string => {
  const ids = [
    topic.topic_id,
    topic.level_2_id,
    topic.level_3_id,
    topic.level_4_id,
  ].filter(Boolean)
  return ids.length ? ids.join("|") : topic.name
}

/** Deepest selected hierarchy name, or the legacy name field. */
export const deepestHierarchyName = (
  names: LevelFieldNames
): string => {
  for (let level = 4; level >= 1; level -= 1) {
    const value = names[LEVEL_NAME_KEYS[level - 1]]
    if (value?.trim()) return value.trim()
  }
  return ""
}

/** Breadcrumb path, e.g. "Algebra › Linear equations". */
export const formatHierarchyPath = (
  topic: Pick<
    HierarchyTopicSelection,
    | "name"
    | "topic"
    | "level_2_name"
    | "level_3_name"
    | "level_4_name"
  >,
  levelLabels?: string[]
): string => {
  const labels = normalizeLabels(levelLabels)
  const parts = [
    topic.topic,
    topic.level_2_name,
    topic.level_3_name,
    topic.level_4_name,
  ].filter((value): value is string => !!value?.trim())

  if (parts.length) return parts.join(" › ")
  return topic.name
}

/** Labelled rows for detail views (Topic: Algebra, Subtopic: …). */
export const hierarchyPathRows = (
  topic: Pick<
    HierarchyTopicSelection,
    | "topic"
    | "level_2_name"
    | "level_3_name"
    | "level_4_name"
  >,
  levelLabels?: string[]
): Array<{ label: string; value: string }> => {
  const labels = normalizeLabels(levelLabels)
  const names = [
    topic.topic,
    topic.level_2_name,
    topic.level_3_name,
    topic.level_4_name,
  ]
  return names
    .map((value, idx) =>
      value?.trim()
        ? { label: labelAt(labels, idx + 1), value: value.trim() }
        : null
    )
    .filter((row): row is { label: string; value: string } => row !== null)
}

export const buildHierarchyTopicFromLevels = (
  values: LevelFieldValues,
  names: LevelFieldNames,
  levelLabels?: string[]
): Omit<HierarchyTopicSelection, "questions"> => {
  const displayName =
    deepestHierarchyName(names) || formatHierarchyPath(
      {
        name: "",
        topic: names.level_1,
        level_2_name: names.level_2,
        level_3_name: names.level_3,
        level_4_name: names.level_4,
      },
      levelLabels ?? DEFAULT_LEVEL_LABELS
    )

  return {
    name: displayName,
    topic_id: values.level_1 || null,
    topic: names.level_1 || null,
    level_2_id: values.level_2 || null,
    level_2_name: names.level_2 || null,
    level_3_id: values.level_3 || null,
    level_3_name: names.level_3 || null,
    level_4_id: values.level_4 || null,
    level_4_name: names.level_4 || null,
  }
}

export const hierarchyValuesFromTopic = (
  topic: HierarchyTopicSelection
): { values: LevelFieldValues; names: LevelFieldNames } => ({
  values: {
    level_1: topic.topic_id || "",
    level_2: topic.level_2_id || "",
    level_3: topic.level_3_id || "",
    level_4: topic.level_4_id || "",
  },
  names: {
    level_1: topic.topic || null,
    level_2: topic.level_2_name || null,
    level_3: topic.level_3_name || null,
    level_4: topic.level_4_name || null,
  },
})
